import { createFileRoute } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Header } from "@/components/universal/header";
import { linkComponents } from "@/components/universal/markdown";
import { useAuth } from "@/auth/use-auth";
import { states } from "@/lib/states";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";

export const Route = createFileRoute(
  "/_private/_patient-360/dispensing-overview",
)({
  component: DispensingOverview,
});

function parseFrontmatter(raw: string): {
  data: Record<string, string>;
  content: string;
} {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };
  const data: Record<string, string> = {};
  for (const line of match[1].split("\n")) {
    const colonIdx = line.indexOf(":");
    if (colonIdx !== -1) {
      data[line.slice(0, colonIdx).trim()] = line.slice(colonIdx + 1).trim();
    }
  }
  return { data, content: match[2] };
}

function parseMarkdownSections(raw: string) {
  const lines = raw.split("\n");
  const sections: { heading: string; body: string }[] = [];
  let preamble = "";
  let currentHeading = "";
  let currentBody: string[] = [];
  let inSection = false;

  for (const line of lines) {
    if (line.startsWith("## ")) {
      if (inSection) {
        sections.push({
          heading: currentHeading,
          body: currentBody.join("\n"),
        });
      }
      currentHeading = line.slice(3);
      currentBody = [];
      inSection = true;
    } else if (!inSection) {
      preamble += line + "\n";
    } else {
      currentBody.push(line);
    }
  }

  if (inSection) {
    sections.push({ heading: currentHeading, body: currentBody.join("\n") });
  }

  return { preamble, sections };
}

const stateNames = new Set(states.map((s) => s.state));
const slugify = (name: string) => name.toLowerCase().replaceAll(" ", "-");

function DispensingOverview() {
  const { authUser } = useAuth();

  const availableStates = !authUser
    ? []
    : authUser.permissions.includes("filter_bypass")
      ? states.map((s) => s.state)
      : Array.from(
          new Set(
            authUser.entities
              .map((e) => e.state_province)
              .filter((s): s is string => !!s && stateNames.has(s)),
          ),
        ).sort();

  const [selected, setSelected] = useState<string>(availableStates[0] ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dispensing-overview", selected],
    queryFn: async () => {
      const res = await fetch(
        `/markdown/dispensing-overview/${slugify(selected)}/index.md`,
      );
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`Failed to load dispensing overview`);
      const text = await res.text();
      if (
        res.headers.get("content-type")?.includes("html") ||
        text.trimStart().toLowerCase().startsWith("<!doctype")
      )
        return null;
      return text;
    },
    enabled: !!selected,
    staleTime: 60 * 60 * 1000,
  });

  return (
    <div>
      <title>{`${selected} Dispensing Overview - Legrande Health Portal`}</title>
      <Header
        crumbs={[
          {
            name: "Dispensing Overview",
            link: { to: "/dispensing-overview" },
          },
        ]}
      />

      <div className="prose max-w-none mx-8">
        {availableStates.length === 0 ? (
          <ComingSoon
            title="Dispensing Overview"
            message="No states with dispensing information are associated with your account."
          />
        ) : (
          <>
            {availableStates.length > 1 && (
              <div className="not-prose mt-4 max-w-xs">
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={pickerOpen}
                      className="w-full justify-between"
                    >
                      {selected || "Select a state"}
                      <ChevronsUpDown className="ml-2 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-(--radix-popover-trigger-width)">
                    <Command>
                      <CommandInput placeholder="Search state..." />
                      <CommandList>
                        <CommandEmpty>No state found.</CommandEmpty>
                        <CommandGroup>
                          {availableStates.map((s) => (
                            <CommandItem
                              key={s}
                              value={s}
                              onSelect={() => {
                                setSelected(s);
                                setPickerOpen(false);
                              }}
                            >
                              <Check
                                className={
                                  selected === s ? "opacity-100" : "opacity-0"
                                }
                              />
                              {s}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {isLoading ? (
              <LoadingSkeleton />
            ) : isError ? (
              <p className="text-destructive mt-6">
                Something went wrong loading this page. Please try again or
                contact support.
              </p>
            ) : data ? (
              <DispensingContent state={selected} raw={data} />
            ) : (
              <ComingSoon
                title={`${selected} Dispensing Overview`}
                message="Content for this state isn't available yet. Check back soon."
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DispensingContent({ state, raw }: { state: string; raw: string }) {
  const { data: frontmatter, content: body } = parseFrontmatter(raw);
  const { preamble, sections } = parseMarkdownSections(body);
  const title = frontmatter.title ?? "Dispensing Overview";

  return (
    <>
      {frontmatter.updated_at && (
        <p className="text-xs text-muted-foreground mt-4">
          Last updated: {frontmatter.updated_at}
        </p>
      )}
      <h2 className="mt-2">{`${state} ${title}`}</h2>

      <ReactMarkdown components={linkComponents}>{preamble}</ReactMarkdown>

      <Accordion type="multiple">
        {sections.map((section) => (
          <AccordionItem key={section.heading} value={section.heading}>
            <AccordionTrigger className="text-base">
              {section.heading}
            </AccordionTrigger>
            <AccordionContent>
              <ReactMarkdown components={linkComponents}>
                {section.body}
              </ReactMarkdown>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <p className="text-sm text-muted-foreground mt-8 border-t pt-4">
        <strong>Disclaimer:</strong> This content is provided for general
        informational and educational purposes only and summarizes publicly
        available {state} statutes and regulations related to prescriber
        dispensing. It is intended to support compliance planning and does not
        address controlled substances. This platform does not provide legal
        advice, regulatory determinations, or guarantees of compliance. Laws,
        regulations, and enforcement practices may change, and their application
        may vary based on specific facts and practice operations. Users are
        responsible for confirming applicability to their individual
        circumstances. For authoritative guidance, legal interpretation, or
        controlled substance requirements, users should consult the {state}{" "}
        Board of Pharmacy, the U.S. Drug Enforcement Administration, and
        qualified legal counsel.
      </p>
    </>
  );
}

function ComingSoon({ title, message }: { title: string; message: string }) {
  return (
    <div className="not-prose mt-8 rounded-md border p-8 text-center">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2">{message}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="not-prose mt-6 space-y-3">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-8 w-96" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );
}
